import { useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { shouldSkipInvalidation } from '@/lib/core/visibility-tracker';
import type {
  TimeTrackingActivityEventWithEmployee,
  TimeTrackingActivityFilters,
  TimeActivityEventType,
} from '@/types/time-tracking-activity';
import { startOfDay, endOfDay, isToday, isYesterday, format } from 'date-fns';

const DEFAULT_LIMIT = 50;

interface UseTimeTrackingActivityLogOptions {
  businessId: string | null;
  filters?: TimeTrackingActivityFilters;
  limit?: number;
  retentionDays?: number; // 7 for basic, 30 for premium
}

interface UseTimeTrackingActivityLogResult {
  events: TimeTrackingActivityEventWithEmployee[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  groupedEvents: Map<string, TimeTrackingActivityEventWithEmployee[]>;
}

/**
 * Hook for fetching and subscribing to time tracking activity log
 * Provides real-time updates via Supabase realtime subscriptions
 */
export function useTimeTrackingActivityLog({
  businessId,
  filters,
  limit = DEFAULT_LIMIT,
  retentionDays = 7, // Default to 7 days for basic users
}: UseTimeTrackingActivityLogOptions): UseTimeTrackingActivityLogResult {
  const queryClient = useQueryClient();

  const queryKey = useMemo(
    () => ['time-tracking-activity-log', businessId, filters, limit, retentionDays],
    [businessId, filters, limit, retentionDays]
  );

  // Build date range filter - respect retention limits
  const dateRange = useMemo(() => {
    const end = new Date();
    const maxRetentionStart = new Date();
    maxRetentionStart.setDate(maxRetentionStart.getDate() - retentionDays);

    if (filters?.dateRange) {
      // Clamp the requested date range to retention limits
      const requestedStart = startOfDay(filters.dateRange.start);
      const effectiveStart = requestedStart < maxRetentionStart ? maxRetentionStart : requestedStart;

      return {
        start: startOfDay(effectiveStart).toISOString(),
        end: endOfDay(filters.dateRange.end).toISOString(),
      };
    }

    // Default to retention period
    return {
      start: startOfDay(maxRetentionStart).toISOString(),
      end: endOfDay(end).toISOString(),
    };
  }, [filters?.dateRange, retentionDays]);

  // Fetch activity log with employee data
  const { data, isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      if (!businessId) return [];

      // First fetch activity logs
      let query = supabase
        .from('time_tracking_activity_log')
        .select('*')
        .eq('business_id', businessId)
        .gte('event_timestamp', dateRange.start)
        .lte('event_timestamp', dateRange.end)
        .order('event_timestamp', { ascending: false })
        .limit(limit);

      // Apply employee filter
      if (filters?.employeeIds && filters.employeeIds.length > 0) {
        query = query.in('employee_id', filters.employeeIds);
      }

      // Apply event type filter
      if (filters?.eventTypes && filters.eventTypes.length > 0) {
        query = query.in('event_type', filters.eventTypes);
      }

      const { data: events, error: queryError } = await query;

      if (queryError) throw queryError;

      if (!events || events.length === 0) return [];

      // Get unique employee IDs (filter out nulls for unassigned shift events)
      const employeeIds = [...new Set(events.map(e => e.employee_id).filter(Boolean))] as string[];
      const actorEmployeeIds = [...new Set(events.map(e => e.actor_employee_id).filter(Boolean))] as string[];
      const allEmployeeIds = [...new Set([...employeeIds, ...actorEmployeeIds])];

      // Fetch employee data
      const { data: employees } = await supabase
        .from('employees')
        .select('id, name, avatar_url')
        .in('id', allEmployeeIds);

      const employeeMap = new Map(employees?.map(e => [e.id, e]) || []);

      // Combine data
      return events.map(event => ({
        ...event,
        employee: event.employee_id ? employeeMap.get(event.employee_id) || null : null,
        actor_employee: event.actor_employee_id ? employeeMap.get(event.actor_employee_id) || null : null,
      })) as TimeTrackingActivityEventWithEmployee[];
    },
    enabled: !!businessId,
    staleTime: 30000, // 30 seconds
  });

  // Real-time subscription for new events
  useEffect(() => {
    if (!businessId) return;

    const channel = supabase
      .channel(`activity-log-${businessId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'time_tracking_activity_log',
          filter: `business_id=eq.${businessId}`,
        },
        () => {
          // Skip invalidation if tab just became visible (prevents refetch storm)
          if (shouldSkipInvalidation()) return;
          // Invalidate and refetch on new events
          queryClient.invalidateQueries({ queryKey });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [businessId, queryClient, queryKey]);

  // Group events by date
  const groupedEvents = useMemo(() => {
    const groups = new Map<string, TimeTrackingActivityEventWithEmployee[]>();

    (data || []).forEach((event) => {
      const eventDate = new Date(event.event_timestamp);
      let groupKey: string;

      if (isToday(eventDate)) {
        groupKey = 'Today';
      } else if (isYesterday(eventDate)) {
        groupKey = 'Yesterday';
      } else {
        groupKey = format(eventDate, 'EEEE, MMMM d');
      }

      if (!groups.has(groupKey)) {
        groups.set(groupKey, []);
      }
      groups.get(groupKey)!.push(event);
    });

    return groups;
  }, [data]);

  return {
    events: data || [],
    isLoading,
    error: error as Error | null,
    refetch,
    groupedEvents,
  };
}

/**
 * Generate a human-readable description for an activity event
 */
export function getActivityEventDescription(
  event: TimeTrackingActivityEventWithEmployee
): string {
  const employeeName = event.employee?.name || 'Unknown employee';
  const metadata = event.metadata || {};

  switch (event.event_type) {
    case 'clock_arrival':
      return `${employeeName} clocked in${metadata.auth_method ? ` via ${metadata.auth_method.toUpperCase()}` : ''}`;

    case 'clock_departure':
      return `${employeeName} clocked out${metadata.auth_method ? ` via ${metadata.auth_method.toUpperCase()}` : ''}`;

    case 'clock_break_start':
      return `${employeeName} started break`;

    case 'clock_break_end':
      return `${employeeName} ended break`;

    case 'manual_edit': {
      const actorName = event.actor_employee?.name || 'Manager';
      const field = metadata.field || 'time';
      return `${actorName} edited ${employeeName}'s ${field} time`;
    }

    case 'manual_delete': {
      const actorName = event.actor_employee?.name || 'Manager';
      return `${actorName} deleted ${employeeName}'s time entry`;
    }

    case 'overtime_alert': {
      const minutes = metadata.overtime_minutes || 0;
      if (minutes > 0) {
        return `${employeeName} has ${minutes} minutes of overtime`;
      }
      return `${employeeName} is approaching overtime`;
    }

    case 'geofence_violation': {
      const distance = metadata.distance_meters || 0;
      const allowed = metadata.allowed_radius || 100;
      return `${employeeName} attempted clock action ${distance}m away (limit: ${allowed}m)`;
    }

    case 'shift_auto_close':
      return `${employeeName}'s shift was auto-closed by system`;

    case 'shift_started': {
      const shiftTitle = metadata.shift_title || 'Shift';
      return `${shiftTitle} started for ${employeeName}`;
    }

    case 'shift_ended': {
      const shiftTitle = metadata.shift_title || 'Shift';
      return `${shiftTitle} ended for ${employeeName}`;
    }

    case 'employee_no_show': {
      const shiftTitle = metadata.shift_title || 'their shift';
      return `${employeeName} did not show up for ${shiftTitle}`;
    }

    case 'unassigned_shift_started': {
      const shiftTitle = metadata.shift_title || 'Unassigned shift';
      return `${shiftTitle} started (no employee assigned)`;
    }

    case 'unassigned_shift_ended': {
      const shiftTitle = metadata.shift_title || 'Unassigned shift';
      return `${shiftTitle} ended (no employee assigned)`;
    }

    default:
      return `Activity for ${employeeName}`;
  }
}
